import React, { useCallback, useEffect, useState } from 'react';
import { useMap, useMapEvents } from 'react-leaflet';
import { getManagedLayerGeoJson } from '../../services/geographyService';
import ManagedGeographyLayer from './ManagedGeographyLayer';

export default function LocalLakesLayer({
  layer, tenantId, interactive = true, consumerModule = 'harvesting',
  onFeatureSelect, selectedIds = [], onViewportChange, featureNoun = 'lakes',
}) {
  const map = useMap();
  const [bbox, setBbox] = useState(null);
  const [data, setData] = useState(null);
  const [message, setMessage] = useState('');
  const update = useCallback(() => {
    if (map.getZoom() < 11) { setBbox(null); return; }
    const bounds = map.getBounds();
    const west = bounds.getWest();
    const offset = 360 * Math.floor((west + 180) / 360);
    const extent = [
      Math.max(-180, west - offset), Math.max(-90, bounds.getSouth()),
      Math.min(180, bounds.getEast() - offset), Math.min(90, bounds.getNorth()),
    ];
    setBbox(extent[0] < extent[2] && extent[1] < extent[3] ? extent.join(',') : null);
  }, [map]);
  useMapEvents({
    movestart: () => { setBbox(null); setData(null); },
    zoomstart: () => { setBbox(null); setData(null); },
    moveend: update,
    zoomend: update,
  });
  useEffect(() => { update(); }, [update]);
  useEffect(() => { onViewportChange?.(bbox); }, [bbox, onViewportChange]);
  useEffect(() => {
    setData(null);
    setMessage('');
    if (!bbox || !tenantId) return undefined;
    let current = true;
    const timer = setTimeout(async () => {
      setMessage(`Loading local ${featureNoun}...`);
      try {
        const response = await getManagedLayerGeoJson(layer._id, {
          tenantId, module: consumerModule, bbox, limit: 3000,
        });
        if (current) {
          setData({ ...response.data, bbox });
          setMessage(response.data.truncated
            ? 'This area has many shapes. Zoom closer to see all.'
            : response.data.features.length ? '' : 'No source shapes in this area.');
        }
      } catch (error) {
        if (current) setMessage(error.response?.data?.message || 'Could not load lake shapes. Pan or zoom to retry.');
      }
    }, 250);
    return () => { current = false; clearTimeout(timer); };
  }, [bbox, layer._id, tenantId, consumerModule, featureNoun]);

  return (
    <>
      {bbox && message && <div className="leaflet-top leaflet-right" style={{ padding: 10, pointerEvents: 'none', maxWidth: '75%' }}>
        <div role="status" style={{ background: '#fff', padding: 8, borderRadius: 6 }}>{message}</div>
      </div>}
      {data?.bbox === bbox && <ManagedGeographyLayer key={bbox} layer={layer} data={data} interactive={interactive}
        onFeatureSelect={onFeatureSelect} selectedIds={selectedIds} />}
    </>
  );
}
